import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, Checkbox, FormControlLabel, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import MainCard from '../../../components/cards/MainCard';
import useDashboardData from '../hooks/useDashboardData';
import { toServerScope } from '../serverScope';
import { computeFlaggedSims, useFlagSimRows } from './FlaggedSimsPage';
import { useGetFlaggedSimAcksQuery } from '../../../store/services/reportLayout.service';
import { DATA_EPOCH } from '../data';
import { EMPTY_CELL } from '../helpers';
import { ExportMenu, RowViewButton } from './DataTable';
import ActiveFilters from './ActiveFilters';
import WidgetState from './WidgetState';
import { PERFORMANCE, formatPerformanceCell, loadHiddenColumns, saveHiddenColumns } from '../performanceConfig';

const PAGE_SIZES = [10, 25, 50, 100];
const EXPORT_LIMIT = 20000;
const ACTIONS_WIDTH = 96;
const tint = (color, pct) => `color-mix(in srgb, ${color} ${pct}%, transparent)`;
const fullName = (r) => `${r.firstName || ''} ${r.lastName || ''}`.trim() || r.email;

function useSimWidgetValues({ scope, period, startDate, endDate }) {
    const serverScope = toServerScope(scope);
    const simsAggQ = useDashboardData({ metric: 'simulations', aggregation: 'sims', period, startDate, endDate, scope: serverScope });
    const riskAggQ = useDashboardData({ metric: 'simulations', aggregation: 'risk', period, startDate, endDate, scope: serverScope });
    const tenancyId = Number(useSelector((st) => st.tenancy.tenancyId));
    const flagAcksQ = useGetFlaggedSimAcksQuery(tenancyId, { skip: !Number.isFinite(tenancyId) || tenancyId <= 0 });
    const flagSimRows = useFlagSimRows({ scope, period, startDate, endDate });
    const flaggedCount = useMemo(() => {
        const acks = flagAcksQ.data?.acks || {};
        return computeFlaggedSims(flagSimRows, { period, startDate, endDate }).filter((f) => !acks[f.sim.name]).length;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flagAcksQ.data, flagSimRows, DATA_EPOCH, period, startDate, endDate]);

    const rows = Array.isArray(simsAggQ.data?.sims) ? simsAggQ.data.sims : null;
    const sum = (k) => (rows || []).reduce((a, r) => a + (r[k] || 0), 0);
    const sent = sum('sent');
    const pct = (n) => (sent ? ((n / sent) * 100).toFixed(1) : '0.0');
    const repeat = riskAggQ.data?.risk?.repeatClickers;
    return {
        'metric-sends': { value: sent.toLocaleString(), sub: `${sum('clicks').toLocaleString()} of ${sent.toLocaleString()} clicked`, queries: [simsAggQ] },
        'metric-click-rate': { value: pct(sum('clicks')), unit: '%', sub: 'Across all sims', queries: [simsAggQ] },
        'metric-second-stage': { value: pct(sum('landings')), unit: '%', sub: 'Credentials entered or download link clicked', queries: [simsAggQ] },
        'metric-repeat-clickers': { value: repeat != null ? repeat.toLocaleString() : EMPTY_CELL, sub: 'Clicked 2+ tests in a row', queries: [riskAggQ] },
        'metric-report-rate': { value: pct(sum('reports')), unit: '%', sub: 'Of total sends', queries: [simsAggQ] },
        'metric-flagged': { value: String(flaggedCount), sub: 'Require attention', queries: [simsAggQ, flagAcksQ] },
    };
}

function useCourseWidgetValues({ scope, period, startDate, endDate }) {
    const courseDetailQ = useDashboardData({ metric: 'courses', aggregation: 'course_detail', period, startDate, endDate, scope: toServerScope(scope) });
    const k = courseDetailQ.data?.courseDetail?.kpis || {};
    const n = (v) => (v ?? 0).toLocaleString();
    const queries = [courseDetailQ];
    return {
        'metric-courses-sent': { value: n(k.sent), sub: `${n(k.completed)} of ${n(k.sent)} completed`, queries },
        'metric-completion-rate': { value: String(k.completionPct ?? 0), unit: '%', sub: `${n(k.completed)} completions`, queries },
        'metric-avg-score': { value: String(k.avgScore ?? 0), unit: '%', sub: 'Across course attempts in the period', queries },
        'metric-pass-mark-rate': { value: String(k.passMarkRate ?? 0), unit: '%', sub: `${n(k.passedCount)} of ${n(k.completedScoredCount)} scored 80+`, queries },
        'metric-pass-rate': { value: String(k.avgPassScore ?? 0), unit: '%', sub: 'Average Score where pass mark was achieved', queries },
        'metric-training-debt': { value: n(k.trainingDebt), sub: 'Overdue assignments still incomplete', queries },
        'metric-course-lag': { value: String(k.courseLag ?? 0), unit: 'd', sub: 'All assignments, open ones at age - cap 30d', queries },
    };
}

function PerformanceWidget({ widget, data, selected, dimmed, onToggle, onMore }) {
    const pickable = !widget.page;
    return (
        <MainCard
            content={false}
            border={false}
            boxShadow
            onClick={pickable ? onToggle : undefined}
            sx={{
                bgcolor: selected ? tint(widget.color, 6) : 'background.paper',
                height: '100%',
                border: '1.5px solid',
                borderColor: selected ? widget.color : 'transparent',
                opacity: dimmed ? 0.55 : 1,
                cursor: pickable ? 'pointer' : 'default',
                transition: 'opacity 150ms, border-color 150ms, background-color 150ms',
                '&:hover': { opacity: dimmed ? 0.9 : 1, borderColor: selected ? widget.color : pickable ? 'divider' : 'transparent' },
            }}
        >
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', gap: 0.5 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>
                    {widget.label}
                </Typography>
                {data.sub && <Typography sx={{ fontSize: 10, color: 'text.secondary', lineHeight: 1.4 }}>{data.sub}</Typography>}
                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography sx={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1, color: 'text.primary' }}>{data.value}</Typography>
                        {data.unit && <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary' }}>{data.unit}</Typography>}
                    </Box>
                    {pickable ? (
                        <Tooltip title={selected ? 'Clear highlight' : 'View in table'}>
                            <IconButton
                                size="small"
                                aria-pressed={selected}
                                aria-label={`${selected ? 'Stop highlighting' : 'Highlight'} ${widget.label} in table`}
                                onClick={(e) => { e.stopPropagation(); onToggle(); }}
                                sx={{
                                    width: 30, height: 28, borderRadius: '8px', border: '1px solid',
                                    borderColor: selected ? widget.color : 'divider',
                                    bgcolor: selected ? widget.color : 'background.paper',
                                    color: selected ? 'background.paper' : 'text.secondary',
                                    '&:hover': { bgcolor: selected ? widget.color : 'action.hover' },
                                }}
                            >
                                <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Button
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onMore(); }}
                            endIcon={<ArrowForwardIcon sx={{ fontSize: 12 }} />}
                            sx={{ fontSize: 11, fontWeight: 500, textTransform: 'none', color: 'text.secondary', minWidth: 0, px: 0.75, '&:hover': { color: 'secondary.main', bgcolor: 'transparent' } }}
                        >
                            More
                        </Button>
                    )}
                </Box>
            </Box>
        </MainCard>
    );
}

function ColumnsMenu({ columns, hidden, temp, onToggle, onShowAll }) {
    const [anchor, setAnchor] = useState(null);
    return (
        <>
            <Button
                size="small"
                variant="outlined"
                startIcon={<ViewColumnIcon sx={{ fontSize: 14 }} />}
                onClick={(e) => setAnchor(e.currentTarget)}
                sx={{
                    borderRadius: '4px', borderColor: 'divider', color: 'text.secondary',
                    fontSize: 11, py: '3px', px: '10px', textTransform: 'none',
                    '&:hover': { borderColor: 'secondary.main', color: 'secondary.main' },
                }}
            >
                Columns
            </Button>
            <Popover
                open={!!anchor}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { p: 1, mt: 0.5, border: '1px solid', borderColor: 'divider', boxShadow: '0 4px 20px rgba(0,0,0,0.10)', borderRadius: '8px', minWidth: 240 } }}
            >
                <Box sx={{ px: 1, py: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider', mb: 0.5 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled' }}>Show columns</Typography>
                    <Button size="small" onClick={onShowAll} sx={{ fontSize: 11, textTransform: 'none', minWidth: 0, p: '2px 4px' }}>Show all</Button>
                </Box>
                {columns.map((c) => (
                    <FormControlLabel
                        key={c.id}
                        control={
                            <Checkbox
                                checked={!hidden.includes(c.id)}
                                onChange={() => onToggle(c.id)}
                                size="small"
                                sx={{ p: 0.5, color: 'text.disabled', '&.Mui-checked': { color: 'secondary.main' } }}
                            />
                        }
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                <Typography sx={{ fontSize: 12 }}>{c.label}</Typography>
                                {temp.includes(c.id) && <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'var(--amber-text)', ml: 'auto' }}>Shown for highlight</Typography>}
                            </Box>
                        }
                        sx={{ display: 'flex', m: 0, px: 1, py: 0.25, borderRadius: '4px', '& .MuiFormControlLabel-label': { flex: 1 }, '&:hover': { bgcolor: 'action.hover' } }}
                    />
                ))}
            </Popover>
        </>
    );
}

function Pager({ page, pageSize, total, onPage, onPageSize }) {
    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    const navSx = {
        minWidth: 24, width: 24, height: 24, p: 0, fontSize: 14, lineHeight: 1,
        border: '1px solid', borderColor: 'divider', borderRadius: '4px', color: 'text.secondary',
        '&:hover': { borderColor: 'secondary.main', color: 'secondary.main' },
    };
    return (
        <Box sx={{ px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid', borderColor: 'divider', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mr: 0.5 }}>Per page</Typography>
                {PAGE_SIZES.map((n) => {
                    const active = pageSize === n;
                    return (
                        <Button key={n} size="small" onClick={() => onPageSize(n)} sx={{
                            minWidth: 0, fontSize: 11, fontWeight: 600, px: 1.25, py: '3px', borderRadius: '4px',
                            border: '1px solid', borderColor: active ? 'secondary.main' : 'divider',
                            bgcolor: active ? 'secondary.main' : 'transparent',
                            color: active ? 'common.white' : 'text.secondary',
                            '&:hover': { borderColor: 'secondary.main', bgcolor: active ? 'secondary.main' : 'transparent' },
                        }}>{n}</Button>
                    );
                })}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                    {total === 0 ? '0 rows' : `Showing ${page * pageSize + 1}-${Math.min((page + 1) * pageSize, total)} of ${total.toLocaleString()}`}
                </Typography>
                {pageCount > 1 && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button size="small" disabled={page === 0} onClick={() => onPage(page - 1)} aria-label="Previous page" sx={navSx}>‹</Button>
                        <Button size="small" disabled={page >= pageCount - 1} onClick={() => onPage(page + 1)} aria-label="Next page" sx={navSx}>›</Button>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function SimsPerformance(props) {
    const widgetValues = useSimWidgetValues(props);
    return <PerformanceView {...props} kind="sims" widgetValues={widgetValues} />;
}

function CoursesPerformance(props) {
    const widgetValues = useCourseWidgetValues(props);
    return <PerformanceView {...props} kind="courses" widgetValues={widgetValues} />;
}

export default function PerformancePage({ kind, ...props }) {
    return kind === 'courses' ? <CoursesPerformance {...props} /> : <SimsPerformance {...props} />;
}

function PerformanceView({ kind, widgetValues, initialWidgetId, go, drill, scope, removeScope, period, startDate, endDate }) {
    const config = PERFORMANCE[kind];
    const pickable = useCallback((id) => config.widgets.some((w) => w.id === id && !w.page), [config]);
    const [selectedId, setSelectedId] = useState(() => (pickable(initialWidgetId) ? initialWidgetId : null));
    const selected = config.widgets.find((w) => w.id === selectedId) || null;
    const [sort, setSort] = useState(() => (selected ? { field: selected.sort, dir: selected.dir } : config.defaultSort));
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [hidden, setHidden] = useState(() => loadHiddenColumns(config.storageKey));
    const [exportRequested, setExportRequested] = useState(false);
    const scrollRef = useRef(null);
    const scrollOnSelect = useRef(false);

    const selectWidget = (id) => {
        const next = id && id !== selectedId ? config.widgets.find((w) => w.id === id) : null;
        setSelectedId(next ? next.id : null);
        setSort(next ? { field: next.sort, dir: next.dir } : config.defaultSort);
        setPage(0);
        scrollOnSelect.current = true;
    };

    const highlightCols = useMemo(() => (selected ? selected.cols : []), [selected]);
    const temp = highlightCols.filter((id) => hidden.includes(id));
    const visibleCols = config.columns.filter((c) => !hidden.includes(c.id) || temp.includes(c.id));

    const toggleColumn = (id) => {
        const next = hidden.includes(id) ? hidden.filter((x) => x !== id) : [...hidden, id];
        setHidden(next);
        saveHiddenColumns(config.storageKey, next);
    };
    const showAll = () => { setHidden([]); saveHiddenColumns(config.storageKey, []); };

    const baseRequest = useMemo(() => ({
        metric: config.metric,
        aggregation: config.aggregation,
        period, startDate, endDate,
        scope: toServerScope(scope),
        sort: { field: sort.field, direction: sort.dir },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [config, period, startDate, endDate, JSON.stringify(scope), sort.field, sort.dir]);
    const pageRequest = useMemo(() => ({ ...baseRequest, limit: pageSize, offset: page * pageSize }), [baseRequest, pageSize, page]);
    const exportRequest = useMemo(() => ({ ...baseRequest, limit: EXPORT_LIMIT, offset: 0 }), [baseRequest]);
    const rowsQ = useDashboardData(pageRequest);
    const exportQ = useDashboardData(exportRequest, { skip: !exportRequested });
    const rows = rowsQ.data?.[config.rowsKey] || [];
    const total = rowsQ.data?.meta?.totalRecords ?? 0;

    useEffect(() => { setPage(0); }, [baseRequest]);

    useEffect(() => {
        if (!scrollOnSelect.current || !scrollRef.current) return;
        scrollOnSelect.current = false;
        const el = scrollRef.current;
        const cells = el.querySelectorAll('th[data-hl="1"]');
        if (!cells.length) { el.scrollLeft = 0; return; }
        const last = cells[cells.length - 1];
        const overflow = last.offsetLeft + last.offsetWidth - el.clientWidth;
        el.scrollLeft = overflow > 0 ? overflow + 24 : 0;
    }, [selectedId, rows]);

    const requestSort = (id) => {
        if (sort.field === id) setSort({ field: id, dir: sort.dir === 'asc' ? 'desc' : 'asc' });
        else setSort({ field: id, dir: id === 'name' || id === 'email' ? 'asc' : 'desc' });
    };

    const exportColumns = useMemo(() => [
        { id: 'name', label: 'Name', accessor: fullName },
        ...visibleCols.map((c) => ({ id: c.id, label: c.label, accessor: (r) => r[c.id], format: (v) => formatPerformanceCell(c, v) ?? '' })),
    ], [visibleCols]);
    const exportRows = exportQ.data?.[config.rowsKey] || rows;

    const sortLabel = sort.field === 'name' ? 'name' : (config.columns.find((c) => c.id === sort.field)?.label || '').toLowerCase();
    const color = selected ? selected.color : 'var(--text)';
    const openUser = (r) => drill && drill('user-detail', fullName(r), { type: 'user', value: r.email });

    const headSx = (c, extra) => ({
        fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
        px: 1.5, py: 1.25, whiteSpace: 'nowrap', userSelect: 'none', position: 'relative',
        borderBottom: '1px solid', borderColor: 'divider', textAlign: c.type === 'string' || c.id === 'name' ? 'left' : 'right',
        color: highlightCols.includes(c.id) || sort.field === c.id ? 'text.primary' : 'text.disabled',
        bgcolor: highlightCols.includes(c.id) ? tint(color, 12) : 'background.paper',
        cursor: 'pointer',
        '&:hover': { color: 'text.primary' },
        ...(highlightCols.includes(c.id) && { '&::before': { content: '""', position: 'absolute', left: 0, right: 0, top: 0, height: 3, bgcolor: color } }),
        ...(temp.includes(c.id) && { borderLeft: `1px dashed ${tint(color, 50)}`, borderRight: `1px dashed ${tint(color, 50)}` }),
        ...extra,
    });
    const stickyActions = { position: 'sticky', left: 0, zIndex: 2, width: ACTIONS_WIDTH, minWidth: ACTIONS_WIDTH, bgcolor: 'background.paper' };
    const stickyName = { position: 'sticky', left: ACTIONS_WIDTH, zIndex: 2, bgcolor: 'background.paper', boxShadow: 'inset -1px 0 0 var(--border)' };

    const sortArrows = (id) => (
        <Box sx={{ display: 'inline-flex', flexDirection: 'column', ml: 0.25, verticalAlign: 'middle' }}>
            <ArrowDropUpIcon sx={{ fontSize: 12, m: '-3px 0', color: sort.field === id && sort.dir === 'asc' ? 'secondary.main' : 'text.disabled' }} />
            <ArrowDropDownIcon sx={{ fontSize: 12, m: '-3px 0', color: sort.field === id && sort.dir === 'desc' ? 'secondary.main' : 'text.disabled' }} />
        </Box>
    );

    return (
        <Box sx={{ px: 3, pb: 6, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
            <Box>
                <Typography component="h1" sx={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', color: 'text.primary' }}>Performance</Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{config.label} - scoped by the date range and filters above</Typography>
            </Box>
            <ActiveFilters scope={scope} removeScope={removeScope} noPadX />
            <Box sx={{ minHeight: 30, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', fontSize: 12, color: 'text.secondary' }}>
                {selected ? (
                    <>
                        Highlighting
                        <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 1, pl: 1.25, pr: 0.5, py: 0.25, borderRadius: 999,
                            border: `1px solid ${tint(selected.color, 45)}`, bgcolor: tint(selected.color, 10), color: 'text.primary', fontWeight: 600,
                        }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: selected.color }} />
                            {selected.label}
                            <IconButton size="small" aria-label="Clear highlight" onClick={() => selectWidget(null)} sx={{ p: 0.25 }}>
                                <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Box>
                        {temp.length > 0 && (
                            <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                                {temp.length} hidden column{temp.length > 1 ? 's' : ''} shown for this highlight
                            </Typography>
                        )}
                    </>
                ) : (
                    <>Select <VisibilityOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} /> on a widget to highlight its stats in the table</>
                )}
            </Box>

            <Box sx={{
                display: 'grid', gap: 1.5,
                gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: `repeat(${Math.ceil(config.widgets.length / 2)}, minmax(0, 1fr))`, xl: `repeat(${config.widgets.length}, minmax(0, 1fr))` },
            }}>
                {config.widgets.map((w) => (
                    <WidgetState key={w.id} queries={widgetValues[w.id].queries} fill>
                        <PerformanceWidget
                            widget={w}
                            data={widgetValues[w.id]}
                            selected={selectedId === w.id}
                            dimmed={!!selectedId && selectedId !== w.id}
                            onToggle={() => selectWidget(w.id)}
                            onMore={() => go && go('flagged-sims', 'Flagged Sims')}
                        />
                    </WidgetState>
                ))}
            </Box>

            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', minWidth: 0 }}>
                <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>
                            {selected ? selected.tableTitle : 'All users'}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: 'text.secondary', mt: 0.25 }}>
                            Ranked by {sortLabel}. Press View to open the user.
                        </Typography>
                    </Box>
                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: 11, color: 'text.disabled', whiteSpace: 'nowrap' }}>{total.toLocaleString()} rows</Typography>
                        <ExportMenu
                            rows={exportRows}
                            columns={exportColumns}
                            title={`${config.label} performance`}
                            count={total}
                            loading={exportQ.isFetching || !exportQ.data}
                            onOpen={() => setExportRequested(true)}
                        />
                        <ColumnsMenu columns={config.columns} hidden={hidden} temp={temp} onToggle={toggleColumn} onShowAll={showAll} />
                    </Box>
                </Box>
                <WidgetState queries={[rowsQ]}>
                    <Box ref={scrollRef} sx={{ overflowX: 'auto' }}>
                        <Box component="table" sx={{ width: '100%', minWidth: 940, borderCollapse: 'separate', borderSpacing: 0, fontSize: 12 }}>
                            <Box component="thead">
                                <Box component="tr">
                                    <Box component="th" sx={{ ...headSx({ id: 'actions', type: 'string' }), ...stickyActions, cursor: 'default', color: 'text.disabled' }}>Actions</Box>
                                    <Box component="th" onClick={() => requestSort('name')} sx={{ ...headSx({ id: 'name', type: 'string' }), ...stickyName }}>
                                        Name{sortArrows('name')}
                                    </Box>
                                    {visibleCols.map((c) => (
                                        <Box
                                            key={c.id}
                                            component="th"
                                            data-hl={highlightCols.includes(c.id) ? '1' : undefined}
                                            aria-sort={sort.field === c.id ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                                            onClick={() => requestSort(c.id)}
                                            sx={headSx(c)}
                                        >
                                            {c.label}{sortArrows(c.id)}
                                            {temp.includes(c.id) && (
                                                <Box component="span" title="Hidden in Columns - shown while this widget is selected" sx={{ display: 'block', fontSize: 9, fontWeight: 600, textTransform: 'none', letterSpacing: 0, color }}>
                                                    Hidden · shown for highlight
                                                </Box>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                            <Box component="tbody">
                                {rows.length === 0 && (
                                    <Box component="tr">
                                        <Box component="td" colSpan={visibleCols.length + 2} sx={{ px: 2, py: 4, textAlign: 'center', color: 'text.disabled' }}>
                                            No users with sends in this period
                                        </Box>
                                    </Box>
                                )}
                                {rows.map((r) => {
                                    const barred = selected?.rowHighlight ? selected.rowHighlight(r) : false;
                                    return (
                                        <Box component="tr" key={r.email} sx={{ '& td': { borderBottom: '1px solid', borderColor: 'divider' }, '&:hover td': { bgcolor: 'action.hover' } }}>
                                            <Box component="td" sx={{ ...stickyActions, px: 1.5, py: 1, boxShadow: barred ? `inset 3px 0 0 ${color}` : undefined }}>
                                                <RowViewButton onClick={() => openUser(r)} />
                                            </Box>
                                            <Box component="td" sx={{ ...stickyName, px: 1.5, py: 1, whiteSpace: 'nowrap', fontWeight: 600, color: 'text.primary' }}>
                                                {fullName(r)}
                                            </Box>
                                            {visibleCols.map((c) => {
                                                const hl = highlightCols.includes(c.id);
                                                const shown = formatPerformanceCell(c, r[c.id]);
                                                return (
                                                    <Box
                                                        key={c.id}
                                                        component="td"
                                                        sx={{
                                                            px: 1.5, py: 1, whiteSpace: 'nowrap',
                                                            textAlign: c.type === 'string' ? 'left' : 'right',
                                                            fontVariantNumeric: 'tabular-nums',
                                                            bgcolor: hl ? tint(color, 8) : undefined,
                                                            color: hl ? color : selected ? 'text.disabled' : 'text.secondary',
                                                            fontWeight: hl ? 600 : 400,
                                                        }}
                                                    >
                                                        {shown ?? EMPTY_CELL}
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Box>
                </WidgetState>
                <Pager
                    page={page}
                    pageSize={pageSize}
                    total={total}
                    onPage={setPage}
                    onPageSize={(n) => { setPageSize(n); setPage(0); }}
                />
            </MainCard>
        </Box>
    );
}
